import type { FC } from "react";
import { useState, useMemo } from "react";
import { Container, ContentContainer, PageTitle } from "@src/core/styles";
import { useQuery } from "@tanstack/react-query";
import { Logs, Workspace } from "@src/core/services";
import { logDate } from "@src/core/utils";
import { Link } from "react-router-dom";
import { APP_ROUTES } from "@src/core/constants";
import { MenuItem, TextField } from "@mui/material";
import { Button } from "@src/components/ui";
import {
  LogsPageWrapper,
  FiltersRow,
  FilterItem,
  LogsTable,
  LogsTableHeader,
  LogsTableRow,
  MethodBadge,
  EmptyState,
  PaginationRow,
} from "./logs-page.styles";

export const LogsListPage: FC = () => {
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>("");
  const [selectedRoute, setSelectedRoute] = useState<string>("");
  const [offset, setOffset] = useState(0);

  const { data: workspacesData } = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => Workspace.getAll(),
  });

  const workspaces = workspacesData?.data.items ?? [];

  const routesForWorkspace = useMemo(() => {
    if (!selectedWorkspace) return [];
    const ws = workspaces.find((w) => w.id === Number(selectedWorkspace));
    return ws?.routes ?? [];
  }, [selectedWorkspace, workspaces]);

  const { data: logsData, isLoading } = useQuery({
    queryKey: ["all-logs", selectedRoute, offset],
    queryFn: () =>
      Logs.getAll({
        routeId: selectedRoute,
        limit: 20,
        offset,
      }),
    enabled: !!selectedRoute,
  });

  const logs = logsData?.data.logs ?? [];
  const totalLogs = logsData?.data.all ?? 0;

  const handleWorkspaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedWorkspace(e.target.value);
    setSelectedRoute("");
    setOffset(0);
  };

  const handleRouteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRoute(e.target.value);
    setOffset(0);
  };

  const handleLoadMore = () => {
    setOffset((prev) => prev + 20);
  };

  return (
    <Container>
      <ContentContainer>
        <div style={{ width: "100%", padding: "0 24px" }}>
          <LogsPageWrapper>
            <PageTitle>Журнал запросов</PageTitle>

            <FiltersRow>
              <FilterItem>
                <TextField
                  select
                  fullWidth
                  label="Пространство"
                  value={selectedWorkspace}
                  onChange={handleWorkspaceChange}
                  size="small"
                  variant="outlined"
                >
                  <MenuItem value="">Выберите пространство</MenuItem>
                  {workspaces.map((ws) => (
                    <MenuItem key={ws.id} value={ws.id.toString()}>
                      {ws.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FilterItem>

              <FilterItem>
                <TextField
                  select
                  fullWidth
                  label="Маршрут"
                  value={selectedRoute}
                  onChange={handleRouteChange}
                  size="small"
                  variant="outlined"
                  disabled={!selectedWorkspace}
                >
                  <MenuItem value="">Выберите маршрут</MenuItem>
                  {routesForWorkspace.map((route) => (
                    <MenuItem key={route.id} value={route.id.toString()}>
                      {route.method.toUpperCase()} — {route.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FilterItem>
            </FiltersRow>

            {!selectedRoute && (
              <EmptyState>
                Выберите пространство и маршрут для просмотра журнала запросов
              </EmptyState>
            )}

            {selectedRoute && logs.length === 0 && !isLoading && (
              <EmptyState>Нет записей для выбранного маршрута</EmptyState>
            )}

            {logs.length > 0 && (
              <>
                <LogsTable>
                  <LogsTableHeader>
                    <span>ID</span>
                    <span>Метод</span>
                    <span>URL</span>
                    <span>Дата</span>
                    <span>Код</span>
                  </LogsTableHeader>
                  {logs.map((log) => (
                    <Link to={`${APP_ROUTES.LOG}/${log.id}`} key={log.id}>
                      <LogsTableRow>
                        <span>#{log.id}</span>
                        <span>
                          <MethodBadge method={log.route?.method}>
                            {log.route?.method?.toUpperCase() ?? "—"}
                          </MethodBadge>
                        </span>
                        <span>{log.route?.url ?? "—"}</span>
                        <span>
                          {logDate(log.time).date} {logDate(log.time).time}
                        </span>
                        <span>{log.route?.http_code ?? "—"}</span>
                      </LogsTableRow>
                    </Link>
                  ))}
                </LogsTable>

                {logs.length < totalLogs && (
                  <PaginationRow>
                    <Button
                      text="Показать ещё"
                      variant="text"
                      borderradius="XXL"
                      onClick={handleLoadMore}
                    />
                  </PaginationRow>
                )}
              </>
            )}
          </LogsPageWrapper>
        </div>
      </ContentContainer>
    </Container>
  );
};
